import { useEffect, useState, type CSSProperties } from 'react'
import { Toaster } from 'sonner'
import { useLang } from '../_metronic/i18n/Metronici18n'

/**
 * Luminous Toast — app-wide toast surface.
 *
 * Wraps Sonner's <Toaster> with the approved "Luminous" skin (frosted-glass
 * card, per-type luminous edge glow, pulsing icon halo + ring, draining
 * progress bar). The visual styling lives in
 * `_metronic/assets/sass/luminousToast.scss`; here we only wire up:
 *   - position / stacking / duration
 *   - theme  (mirrors the app's `data-bs-theme`, live via MutationObserver)
 *   - dir    (mirrors the active language from i18n context)
 *   - the per-type glyph icons and Sonner classNames the SCSS targets
 *
 * All existing `toast.success / error / info / warning(...)` call sites keep
 * working unchanged — we style Sonner's rendered DOM, not the call API.
 */

const readTheme = (): 'dark' | 'light' =>
  document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light'

const ICONS = {
  info: <span className='lt-glyph'>i</span>,
  success: <span className='lt-glyph'>✓</span>,
  warning: <span className='lt-glyph'>!</span>,
  error: <span className='lt-glyph'>✕</span>,
}

export function LuminousToaster() {
  const langCtx = useLang()
  const dir = langCtx?.lang?.dir === 'rtl' ? 'rtl' : 'ltr'

  const [theme, setTheme] = useState<'dark' | 'light'>(readTheme)

  useEffect(() => {
    const update = () => setTheme(readTheme())
    update()
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    return () => observer.disconnect()
  }, [])

  return (
    <Toaster
      position='top-center'
      theme={theme}
      dir={dir}
      visibleToasts={3}
      gap={12}
      offset={20}
      closeButton
      icons={ICONS}
      style={{ '--width': '392px' } as CSSProperties}
      toastOptions={{
        duration: 6000,
        classNames: {
          toast: 'lt-toast',
          title: 'lt-title',
          description: 'lt-desc',
          content: 'lt-content',
          icon: 'lt-icon',
          closeButton: 'lt-close',
          actionButton: 'lt-action',
          cancelButton: 'lt-cancel',
        },
      }}
    />
  )
}
